import { useCurrentUserOrgs } from './orgs'
import { getCurrentUser } from '@/utils/auth/UserProvider'
import { isFreeTier } from '@/utils/utils'
import { UUID } from 'crypto'

/**
 * Hook to check if the current org's subscription is paused.
 * Returns pause state info for use in UI components.
 */
export function usePausedOrg() {
  const { user, token, orgs, isLoading: userLoading } = getCurrentUser()
  const { orgData, isLoading: orgsLoading } = useCurrentUserOrgs(
    token as string,
    user,
    orgs as UUID[],
  )

  const isLoading = userLoading || orgsLoading

  // Get the selected org or first org
  const selectedOrgUuid = user?.selected_org_uuid
  let currentOrg = null
  if (orgData && orgData.length > 0) {
    currentOrg = selectedOrgUuid
      ? orgData.find((org) => 'uuid' in org && org.uuid === selectedOrgUuid)
      : orgData[0]
    // Filter out error objects
    if (currentOrg && 'error' in currentOrg) {
      currentOrg = null
    }
  }

  const isPaused = currentOrg?.subscription_status === 'paused'
  const isCancelled =
    currentOrg?.subscription_status === 'cancelled' ||
    currentOrg?.membership === 'Cancelled'
  const resumeDate = currentOrg?.pause_until_date || null
  const isPayerChild =
    currentOrg?.payment_type === null && Boolean(currentOrg?.is_child_of)

  // Format the resume date for display
  const formattedResumeDate = resumeDate
    ? new Date(resumeDate).toLocaleDateString('en-NZ', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : null

  return {
    isPaused,
    isCancelled,
    resumeDate,
    formattedResumeDate,
    canWrite: !isPaused && !isCancelled,
    isPayerChild,
    parentOrgName: currentOrg?.parent_org_name || null,
    isLoading,
    orgUuid: currentOrg?.uuid,
    maxTotalScans: currentOrg?.max_total_scans,
    // Scan caps apply to Free tier only; see hasFreeUserReachedScanLimit.
    // An org we haven't loaded reads as Free, but its maxTotalScans is
    // undefined too, so no cap is applied until we actually know both.
    isFreeTierOrg: isFreeTier(currentOrg ?? null),
    isAnnualSubscriber: currentOrg?.is_annual_subscriber === true,
  }
}
